import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Eye, ShieldWarning, Bug, Lightbulb, ChartLineUp, PlugsConnected,
  ArrowLeft, SignOut, CaretRight, SquaresFour, Gear, NewspaperClipping, Buildings,
} from "@phosphor-icons/react";
import { BrandMark } from "@/components/Brand";
import { getIndustry } from "@/lib/industries";

import ProjectOverview from "@/components/lenses/ProjectOverview";
import AssumptionLens from "@/components/lenses/AssumptionLens";
import RiskLens from "@/components/lenses/RiskLens";
import IssueLens from "@/components/lenses/IssueLens";
import SolutionLens from "@/components/lenses/SolutionLens";
import InsightsLens from "@/components/lenses/InsightsLens";
import IntegrationLens from "@/components/lenses/IntegrationLens";
import ProjectBrief from "@/components/lenses/ProjectBrief";

const LENSES = [
  { key: "overview", label: "Overview", icon: SquaresFour, n: "00", color: "#0020F5" },
  { key: "assumption", label: "Assumption", icon: Eye, n: "01", color: "#0020F5" },
  { key: "risk", label: "Risk", icon: ShieldWarning, n: "02", color: "#FF3B30" },
  { key: "issue", label: "Issue", icon: Bug, n: "03", color: "#FF9500" },
  { key: "solution", label: "Solution", icon: Lightbulb, n: "04", color: "#00C7BE" },
  { key: "insights", label: "Insights", icon: ChartLineUp, n: "05", color: "#7C3AED" },
  { key: "integration", label: "Integration", icon: PlugsConnected, n: "06", color: "#0020F5" },
  { key: "brief", label: "Weekly Brief", icon: NewspaperClipping, n: "07", color: "#09090B" },
];

export default function ProjectView() {
  const { projectId, lens } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bumped any time an upload completes — lens components key/refetch off this
  const [dataVersion, setDataVersion] = useState(0);
  const activeLens = lens || "overview";

  useEffect(() => {
    api.get(`/projects/${projectId}`).then(({ data }) => {
      setProject(data);
    }).catch(() => {
      toast.error("Project not found");
      navigate("/dashboard");
    }).finally(() => setLoading(false));
  }, [projectId, navigate]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-zinc-500">Loading…</div>;
  }
  if (!project) return null;

  const refreshAll = () => setDataVersion((v) => v + 1);

  const renderLens = () => {
    switch (activeLens) {
      case "overview": return <ProjectOverview key={`ov-${dataVersion}`} projectId={projectId} />;
      case "assumption": return <AssumptionLens key={`as-${dataVersion}`} projectId={projectId} />;
      case "risk": return <RiskLens key={`ri-${dataVersion}`} projectId={projectId} />;
      case "issue": return <IssueLens key={`is-${dataVersion}`} projectId={projectId} />;
      case "solution": return <SolutionLens key={`so-${dataVersion}`} projectId={projectId} />;
      case "insights": return <InsightsLens key={`in-${dataVersion}`} projectId={projectId} />;
      case "integration": return <IntegrationLens key={`it-${dataVersion}`} projectId={projectId} onImported={refreshAll} />;
      case "brief": return <ProjectBrief key={`br-${dataVersion}`} projectId={projectId} />;
      default: return <ProjectOverview key={`ov-${dataVersion}`} projectId={projectId} />;
    }
  };

  const isOverview = activeLens === "overview";

  return (
    <div className="min-h-screen bg-zinc-100 flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white border-r border-zinc-200 sticky top-0 h-screen flex flex-col" data-testid="lens-sidebar">
        <div className="h-16 px-5 flex items-center border-b border-zinc-200">
          <BrandMark size={32} testid="project-brand" tagline="Project Intelligence" />
        </div>

        <Link to="/dashboard" className="px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-2 border-b border-zinc-200" data-testid="back-to-dashboard">
          <ArrowLeft size={14} weight="bold" /> All projects
        </Link>

        <div className="px-5 py-4 border-b border-zinc-200">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Project</div>
          <div className="font-display font-bold text-lg mt-1 leading-tight line-clamp-2" data-testid="project-name">{project.name}</div>
        </div>

        <nav className="flex-1 overflow-y-auto py-2" data-testid="lens-nav">
          {LENSES.map((l) => {
            const Icon = l.icon;
            const active = activeLens === l.key;
            return (
              <Link
                key={l.key}
                to={`/projects/${projectId}/${l.key}`}
                className={`group flex items-center justify-between px-5 py-3 border-l-2 transition-colors ${
                  active ? "border-zinc-950 bg-zinc-950 text-white" : "border-transparent hover:bg-zinc-50"
                }`}
                data-testid={`sidebar-lens-${l.key}`}
              >
                <div className="flex items-center gap-3">
                  <Icon size={18} weight={active ? "fill" : "duotone"} style={{ color: active ? "#fff" : l.color }} />
                  <div>
                    <div className={`text-[10px] tracking-[0.2em] uppercase font-bold ${active ? "text-zinc-400" : "text-zinc-500"}`}>{l.n}</div>
                    <div className={`text-sm font-semibold ${active ? "text-white" : "text-zinc-900"}`}>{l.label} Lens</div>
                  </div>
                </div>
                <CaretRight size={14} weight="bold" className={active ? "text-white" : "text-zinc-400 group-hover:text-zinc-700"} />
              </Link>
            );
          })}
        </nav>

        <div className="p-5 border-t border-zinc-200">
          <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-zinc-500">{user?.full_name}</div>
          <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          <button
            onClick={() => { logout(); navigate("/"); }}
            className="mt-3 inline-flex items-center gap-2 h-9 px-3 border border-zinc-300 text-xs font-medium hover:bg-zinc-100"
            data-testid="project-logout-button"
          >
            <SignOut size={14} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="bg-white border-b border-zinc-200 sticky top-0 z-20">
          <div className="px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="font-display font-black tracking-tight text-2xl">{project.name}</h1>
                <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-zinc-500 mt-0.5">
                  AI-Driven Project Intelligence Through LENSES · {LENSES.find(l => l.key === activeLens)?.label}
                </div>
              </div>
              {project.industry && project.industry !== "general" && (
                <span
                  className="hidden md:inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase font-bold px-2.5 h-6 text-white"
                  style={{ background: getIndustry(project.industry).accent }}
                  data-testid="project-industry-badge"
                  title={getIndustry(project.industry).valueProp}
                >
                  <Buildings size={10} weight="fill" />
                  {getIndustry(project.industry).label}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 h-7 bg-[#DCFCE7] text-[#16A34A] text-[11px] font-bold uppercase tracking-wider" data-testid="ai-engine-active">
                <span className="w-1.5 h-1.5 bg-[#16A34A] rounded-full" /> AI Engine Active
              </span>
              <button className="h-9 px-3 inline-flex items-center gap-2 border border-zinc-300 text-sm font-medium hover:bg-zinc-100" data-testid="project-settings-button">
                <Gear size={14} weight="bold" /> Settings
              </button>
            </div>
          </div>
        </div>

        <div className={isOverview ? "p-6" : "p-8"}>
          {renderLens()}
        </div>
      </main>
    </div>
  );
}
